import { createReference, getReferenceString } from '@medplum/core';
import { useCallback, useState } from 'react';
import { fetchBot } from '@/services/services';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import { Encounter, Practitioner, Reference } from '@medplum/fhirtypes';
import { alert } from '@/services/alert';
import { useTimelineContext } from './useTimelineContext';

export function useDeleteEncounterDraft() {
  const medplum = useMedplum();
  const profile = useMedplumProfile() as Practitioner | undefined;
  const { timelineContext } = useTimelineContext();
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteEncounterDraft = useCallback(
    async (encounter: Encounter) => {
      if (!encounter.subject) {
        alert.error("Couldn't delete chart: missing patient reference");
        return;
      }

      if (!profile) {
        alert.error("Couldn't delete chart: missing practitioner profile");
        return;
      }

      setIsDeleting(true);
      try {
        await fetchBot(medplum, 'delete-encounter-draft', {
          encounter: createReference(encounter),
          patient: encounter.subject as Reference<any>,
          practitioner: createReference(profile),
        });

        timelineContext.refresh();

        const encounterReference = createReference(encounter);
        const referenceString = getReferenceString(encounterReference);
        if (referenceString) {
          // If the deleted encounter was currently focused, clear focus by navigating to the patient root
          // This is a no-op for now as the timeline refresh will remove the item.
        }
      } catch (error) {
        alert.error(`Couldn't delete chart: ${error instanceof Error ? error.message : 'Unknown error'}`);
      } finally {
        setIsDeleting(false);
      }
    },
    [medplum, profile, timelineContext]
  );

  return {
    isDeleting,
    handleDeleteEncounterDraft,
  };
}
